import { useState, useEffect, useRef } from 'react'

type TimerProps = {
  from: number
  to?: number
  intervalSecond?: number
  finished: (status: boolean) => void
}

type ReturnValue = {
  value: number
  start: () => void
  pause: () => void
  restart: (from?: number) => void
  stop: () => void
}
/**
 * receives three values and a callback
 * @param {number} from starts the timer from this value
 * @param {number} to continues countdown until it reaches 0
 * @param {number} intervalSecond interval between times
 * @param {Function} finished a callback that will be called with true when the value of timer reaches the value passed as to and will be called with false when timer restarts
 * @example
js
 * const [timerFinish, setTimerFinish] = useState(false)
 * const timer = useTimer({from: 60, to: 0, intervalSecond: 1, finished: (value) => setTimerFinish(true)})
 * timer.start() // call it to start the timer
 * timer.pause() // call it to pause the timer
 * timer.stop() // call it to stop the timer. difference between stop and pause is that stop will reset value to from and pause at the same time
 * timer.restart(120) // reset the timer to an optional value. If the value is not passed, from will be used instead
 * 
 * @returns {ReturnValue}
 */
const useTimer = ({
  from,
  to = 0,
  intervalSecond = 1,
  finished,
}: TimerProps): ReturnValue => {
  const [timer, setTimer] = useState<number>(from)
  const timerRef = useRef<number>(from)
  const interval = useRef<NodeJS.Timeout>()

  const start = () => {
    if (!interval.current) {
      interval.current = setInterval(() => {
        setTimer((timer) => {
          timerRef.current = timer - intervalSecond
          return timer - intervalSecond
        })
      }, intervalSecond * 1000)
    }
  }

  const pause = () => {
    clearInterval(interval.current)
    interval.current = undefined
  }

  const stop = () => {
    clearInterval(interval.current)
    interval.current = undefined
    setTimer(from)
    timerRef.current = from
  }

  const restart = (fromArg?: number) => {
    finished(false)
    clearInterval(interval.current)
    interval.current = undefined
    setTimer(fromArg ?? from)
    timerRef.current = fromArg ?? from
    start()
  }

  useEffect(() => {
    return () => {
      clearInterval(interval.current)
      interval.current = undefined
    }
  }, [])

  useEffect(() => {
    if (timer <= to) {
      finished(true)
      clearInterval(interval.current)
      interval.current = undefined
    }
  }, [timer, finished, to])

  return {
    value: timer,
    start,
    pause,
    restart,
    stop,
  }
}

export default useTimer
